import React, { Fragment } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Link from 'shared/SlashLink';
import locations from '../../data/locations';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import { BOOKING_URL, BOOKING_ENABLED } from '../../config/booking';
import { trackEvent } from '../../utils/analytics';

const ClickHandler = () => window.scrollTo(10, 0);

const CONSTRUCTION = [
    { label: 'Low-Voltage & Structured Cabling', to: '/service-single/Low-Voltage-and-Structured-Cabling' },
    { label: 'Surveillance Systems', to: '/service-single/Surveillance-Systems' },
    { label: 'Door Access Systems', to: '/service-single/Door-Access-Systems' },
    { label: 'Fire & Life Safety', to: '/service-single/Fire-and-Life-Safety' },
    { label: 'Computer Networks & Common Areas', to: '/it-services' },
];
const BUSINESS = [
    { label: 'Managed IT Services', to: '/it-services' },
    { label: 'Cyber Security', to: '/cyber-security-scorecard' },
    { label: 'AI Business Integration', to: '/service-single/AI-Business-Integration' },
    { label: 'Web Development', to: '/service-single/Web-Development' },
    { label: 'Same Day Websites', to: '/same-day-websites' },
];

const LocationPage = () => {
    const { slug } = useParams();
    const loc = locations.find((l) => l.slug === slug);

    usePageSeo({
        title: loc ? `Low-Voltage & Managed IT in ${loc.city}, CA` : 'Service Areas',
        description: loc
            ? `BUILDLAB serves ${loc.city}, ${loc.county} with low-voltage design, surveillance, managed IT, and cyber security — for affordable-housing developers and businesses across ${loc.region}.`
            : 'BUILDLAB technology consulting & design service areas across California.',
        keywords: loc
            ? `low voltage contractor ${loc.city}, managed IT ${loc.city}, structured cabling ${loc.city}, surveillance ${loc.city}, affordable housing technology ${loc.county}, IT support ${loc.city} California, BUILDLAB`
            : 'BUILDLAB service areas California',
    });

    if (!loc) return <Navigate to="/contact" replace />;

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={`${loc.city}, California`} pagesub={loc.county} pageTop={'Service Area'} />
                <section className="section_space bg-light">
                    <div className="container">
                        <div className="row mb-5">
                            <div className="col-lg-9">
                                <h2 className="mb-3">Technology consulting &amp; design in {loc.city}, CA</h2>
                                <p>
                                    {loc.blurb} BUILDLAB designs, builds, and runs the technology behind {loc.county} projects and
                                    businesses — coordinated across every trade, by a single accountable vendor. Whether you are a
                                    developer planning a multifamily or affordable-housing build in {loc.region}, or a {loc.city}{' '}
                                    business that needs IT that actually works, we cover the full stack.
                                </p>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-lg-6 mb-4">
                                <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '28px 30px', height: '100%' }}>
                                    <h3 className="mb-3">For {loc.city} developers &amp; construction</h3>
                                    <p className="text-muted" style={{ fontSize: '.92rem' }}>
                                        Low-voltage and life-safety systems for multifamily, affordable housing, and commercial builds —
                                        designed early, coordinated with your GC and architect.
                                    </p>
                                    <ul className="icon_list unordered_list_block">
                                        {CONSTRUCTION.map((s) => (
                                            <li key={s.label}>
                                                <Link onClick={ClickHandler} to={s.to}>
                                                    <span className="icon_list_icon"><i className="fa-solid fa-circle fa-fw" aria-hidden="true" style={{ color: 'var(--bs-primary)', fontSize: '.5rem' }}></i></span>
                                                    <span className="icon_list_text">{s.label}</span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                            <div className="col-lg-6 mb-4">
                                <div style={{ background: '#fff', border: '1px solid var(--bs-border-color)', borderRadius: 16, padding: '28px 30px', height: '100%' }}>
                                    <h3 className="mb-3">For {loc.city} businesses</h3>
                                    <p className="text-muted" style={{ fontSize: '.92rem' }}>
                                        Managed IT, security, and AI for {loc.city} companies that want a proactive partner, not a
                                        break-fix vendor.
                                    </p>
                                    <ul className="icon_list unordered_list_block">
                                        {BUSINESS.map((s) => (
                                            <li key={s.label}>
                                                <Link onClick={ClickHandler} to={s.to}>
                                                    <span className="icon_list_icon"><i className="fa-solid fa-circle fa-fw" aria-hidden="true" style={{ color: 'var(--bs-primary)', fontSize: '.5rem' }}></i></span>
                                                    <span className="icon_list_text">{s.label}</span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="row mt-2">
                            <div className="col-lg-12">
                                <div style={{ background: 'var(--bs-dark)', borderRadius: 16, padding: '34px 36px', color: '#fff' }}>
                                    <div className="row align-items-center">
                                        <div className="col-lg-8 mb-3 mb-lg-0">
                                            <h3 className="text-white mb-2">Planning a project in {loc.city}?</h3>
                                            <p className="mb-0" style={{ opacity: 0.9 }}>
                                                Start with a free estimate — model your project budget, score a tax-credit application,
                                                or size your IT, then talk to a {loc.city}-area specialist.
                                            </p>
                                            <div style={{ marginTop: 12, fontSize: '.9rem' }}>
                                                <Link to="/feasibility-pro-forma" style={{ color: '#FFC9A3', marginRight: 16 }}>Feasibility Pro Forma</Link>
                                                <Link to="/low-voltage-budget-estimator" style={{ color: '#FFC9A3', marginRight: 16 }}>Low-Voltage Budget</Link>
                                                <Link to="/it-cost-calculator" style={{ color: '#FFC9A3' }}>IT Cost Calculator</Link>
                                            </div>
                                        </div>
                                        <div className="col-lg-4 text-lg-end">
                                            <Link to="/contact" onClick={ClickHandler} className="btn btn-primary" style={{ marginBottom: 10 }}>
                                                <span className="btn_label" data-text="Get a free consultation">Get a free consultation</span>
                                            </Link>
                                            {BOOKING_ENABLED && (
                                                <div>
                                                    <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('book_consult', { tool: `Location: ${loc.city}` })} style={{ color: '#FFC9A3', fontWeight: 600, fontSize: '.9rem' }}>
                                                        Book a 15-min consult <i className="fa-solid fa-arrow-up-right" aria-hidden="true" style={{ fontSize: '.72rem' }}></i>
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <p className="text-center mt-4" style={{ fontSize: '.9rem' }}>
                            BUILDLAB serves clients across California.{' '}
                            <Link onClick={ClickHandler} to="/locations">See all service areas</Link>.
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default LocationPage;
